/**
 * Update listing page: select listing, load MLB item + product; show diff (DB vs MLB), attributes, pictures, Edit listing suppliers.
 */
import React, { useRef, useMemo, useState, useEffect } from 'react';
import { Container, Form, Spinner, Card, Table, Button } from 'react-bootstrap';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { useAppStore } from '../../store/appStore';
import { DropdownField, useClickOutside, InfoTableCard, CATEGORIES_QUERY_KEY, LIST_STALE_TIME_MS } from '../common';
import { getListings } from '../../services/listingsService';
import { getMlbItem } from '../../services/mlbItemsService';
import { getProduct } from '../../services/productService';
import { getProductCategories } from '../../services/productCategoryService';
import { getProductPictures, getPicturePublicUrl } from '../../services/productPicturesService';
import { getProductAdditionalDetailsMlbAttributes } from '../../services/productAdditionalDetailService';
import { getProductSuppliers } from '../../services/productSuppliersService';
import { getListingProductSuppliers } from '../../services/listingsProductSuppliersService';
import { buildListingTitle, getEligibleProductSuppliersForListing } from '../listingsComponents/listingUtils';
import PickListingSuppliersModal from '../listingsComponents/PickListingSuppliersModal';
import ListingPictureCarousel from '../listingsComponents/ListingPictureCarousel';
import { scalarDiff, getMlbWarranty, mergeAttributesWithDiff } from './updateListingDiffUtils';

const LISTINGS_QUERY_KEY = ['listings'];
const MLB_ITEM_STALE_MS = 2 * 60 * 1000;

function mlbItemToRowsWithDiff(item, product, ourTitle, allocatedTotal) {
  if (!item || typeof item !== 'object') return [];
  const saleTermsSummary = Array.isArray(item.sale_terms) && item.sale_terms.length > 0
    ? item.sale_terms.map((t) => t.value_name || t.id).join(', ')
    : null;
  const shippingMode = item.shipping && typeof item.shipping === 'object' ? item.shipping.mode : null;
  const mlbWarranty = getMlbWarranty(item.sale_terms);
  const dbWarranty = product && product.warranty != null ? String(product.warranty).trim() : null;
  const titleDiff = scalarDiff(ourTitle && ourTitle !== '—' ? ourTitle : null, item.title);
  const warrantyDiff = scalarDiff(dbWarranty, mlbWarranty);
  const qtyDiff = (allocatedTotal != null && item.available_quantity != null && Number(allocatedTotal) !== Number(item.available_quantity))
    ? 'diff_value'
    : null;

  /* Condition shown only in Attributes (ITEM_CONDITION), not as standalone field */
  const rows = [
    { label: 'MLB ID', value: item.id },
    { label: 'Title', value: item.title, diffState: titleDiff },
    { label: 'Price', value: item.price },
    { label: 'Currency', value: item.currency_id },
    { label: 'Available quantity', value: item.available_quantity, diffState: qtyDiff },
    { label: 'Sold quantity', value: item.sold_quantity },
    { label: 'Status', value: item.status },
    { label: 'Buying mode', value: item.buying_mode },
    { label: 'Listing type', value: item.listing_type_id },
    { label: 'Category ID', value: item.category_id },
    { label: 'Sale terms', value: saleTermsSummary },
    { label: 'Warranty (MLB)', value: mlbWarranty, diffState: warrantyDiff },
    { label: 'Shipping mode', value: shippingMode }
  ];
  return rows.filter((r) => r.value !== undefined && r.value !== null && r.value !== '');
}

export default function UpdateListingPage() {
  const { token } = useAuth();
  const updateListingPage = useAppStore((s) => s.updateListingPage);
  const setUpdateListingPage = useAppStore((s) => s.setUpdateListingPage);
  const { listingSearchQuery, showListingDropdown, selectedListing, showEditListingSuppliersModal, editListingSupplierSelection } = updateListingPage;
  const listingDropdownRef = useRef(null);
  const [listingPictureIndex, setListingPictureIndex] = useState(0);

  const { data: listingsResponse, isLoading: listingsLoading } = useQuery({
    queryKey: LISTINGS_QUERY_KEY,
    queryFn: () => getListings(token),
    enabled: !!token
  });

  const mlId = selectedListing?.marketplace_listing_id;
  const productId = selectedListing?.product_id;
  const listingId = selectedListing?.id;

  const { data: mlbItemResponse, isLoading: mlbItemLoading, error: mlbItemError } = useQuery({
    queryKey: ['mlb-item', mlId],
    queryFn: () => getMlbItem(token, mlId),
    enabled: !!token && !!mlId,
    staleTime: MLB_ITEM_STALE_MS,
    retry: false
  });

  const { data: productResponse, isLoading: productLoading } = useQuery({
    queryKey: ['product', productId],
    queryFn: () => getProduct(token, String(productId)),
    enabled: !!token && productId != null,
    staleTime: LIST_STALE_TIME_MS
  });

  const { data: categoriesResponse } = useQuery({
    queryKey: CATEGORIES_QUERY_KEY,
    queryFn: () => getProductCategories(token),
    enabled: !!token && !!selectedListing
  });

  const { data: productPicturesResponse } = useQuery({
    queryKey: ['product-pictures', productId],
    queryFn: () => getProductPictures(token, productId),
    enabled: !!token && !!productId
  });

  const { data: productAttrsResponse } = useQuery({
    queryKey: ['product-additional-details-mlb', productId],
    queryFn: () => getProductAdditionalDetailsMlbAttributes(token, productId),
    enabled: !!token && !!productId
  });

  const { data: productSuppliersResponse } = useQuery({
    queryKey: ['product-suppliers', productId],
    queryFn: () => getProductSuppliers(token, { product_id: productId }),
    enabled: !!token && !!productId
  });

  const { data: listingLinksResponse } = useQuery({
    queryKey: ['listings-product-suppliers', listingId],
    queryFn: () => getListingProductSuppliers(token, { listing_id: listingId }),
    enabled: !!token && !!listingId
  });

  const listings = listingsResponse?.listings ?? [];
  const product = productResponse?.product ?? null;
  const categories = categoriesResponse?.product_categories ?? [];
  const productPictures = productPicturesResponse?.product_pictures ?? [];
  const productAttrs = productAttrsResponse?.product_additional_details ?? [];
  const productSuppliers = productSuppliersResponse?.product_suppliers ?? [];
  const listingLinks = listingLinksResponse?.listing_product_suppliers ?? [];

  const eligibleProductSuppliers = useMemo(
    () => getEligibleProductSuppliersForListing(productSuppliers),
    [productSuppliers]
  );

  const ourTitle = useMemo(() => buildListingTitle(product), [product]);

  const allocatedTotal = useMemo(() => {
    return listingLinks.reduce((sum, link) => sum + (Number(link.allocated_amount) || 0), 0);
  }, [listingLinks]);

  const initialSelectionForEditModal = useMemo(() => {
    const saved = listingId && editListingSupplierSelection[listingId] && Object.keys(editListingSupplierSelection[listingId]).length > 0
      ? editListingSupplierSelection[listingId]
      : null;
    if (saved) return saved;
    const byId = {};
    listingLinks.forEach((link) => {
      const psId = link.products_suppliers_id;
      if (psId == null) return;
      byId[psId] = {
        selected_quantity: Number(link.allocated_amount) || 0,
        priority: link.priority != null ? parseInt(String(link.priority), 10) || 0 : 0
      };
    });
    return byId;
  }, [listingId, listingLinks, editListingSupplierSelection]);

  const filteredListings = useMemo(() => {
    if (!listingSearchQuery.trim()) return listings;
    const q = listingSearchQuery.trim().toLowerCase();
    return listings.filter((l) => {
      const title = (l.title || '').toLowerCase();
      const id = String(l.id || '');
      const mlIdVal = (l.marketplace_listing_id || '').toLowerCase();
      return title.includes(q) || id.includes(q) || mlIdVal.includes(q);
    });
  }, [listings, listingSearchQuery]);

  const categoryDisplay = useMemo(() => {
    if (!product || categories.length === 0) return { categoryName: null, subCategoryName: null };
    const row = categories.find(
      (r) => r.id === product.category_id && r.sub_category_id === product.sub_category_id
    );
    return {
      categoryName: row?.category_name ?? null,
      subCategoryName: row?.sub_category_name ?? null
    };
  }, [product, categories]);

  const basicInfoRows = product
    ? [
        { label: 'Name', value: product.name },
        { label: 'Brand', value: product.brand },
        { label: 'Model', value: product.product_model },
        { label: 'SKU', value: product.sku },
        { label: 'Description', value: product.description }
      ]
    : [];

  const classificationRows = product
    ? [
        { label: 'Condition', value: product.condition },
        { label: 'Warranty', value: product.warranty },
        { label: 'Category', value: categoryDisplay.categoryName },
        { label: 'Sub-category', value: categoryDisplay.subCategoryName }
      ]
    : [];

  const mlbItem = mlbItemResponse?.item ?? null;
  const mlbItemRows = useMemo(
    () => mlbItemToRowsWithDiff(mlbItem, product, ourTitle, allocatedTotal),
    [mlbItem, product, ourTitle, allocatedTotal]
  );

  /** Product pictures only (source of truth is our DB; MLB URLs change after upload so we don't show or match them). */
  const listingPictures = useMemo(() => {
    const list = productPicturesResponse?.product_pictures ?? [];
    return list
      .map((p) => ({ source: p.picture_url || getPicturePublicUrl(p.picture_path) }))
      .filter((p) => p.source);
  }, [productPicturesResponse]);

  const attributesWithDiff = useMemo(
    () => mergeAttributesWithDiff(productAttrs, mlbItem?.attributes || []),
    [productAttrs, mlbItem?.attributes]
  );

  useEffect(() => {
    setListingPictureIndex(0);
  }, [selectedListing?.id, listingPictures.length]);

  useClickOutside(listingDropdownRef, () =>
    setUpdateListingPage((prev) => ({ ...prev, showListingDropdown: false }))
  );

  const handleListingSearchChange = (e) => {
    setUpdateListingPage({
      listingSearchQuery: e.target.value,
      selectedListing: null,
      showListingDropdown: true
    });
  };

  const handleSelectListing = (listing) => {
    setUpdateListingPage({
      listingSearchQuery: listing.title || `Listing #${listing.id}`,
      selectedListing: listing,
      showListingDropdown: false
    });
  };

  const handleOpenEditSuppliersModal = () => {
    setUpdateListingPage((prev) => ({ ...prev, showEditListingSuppliersModal: true }));
  };

  const handleSaveEditSuppliersModal = (entries) => {
    if (!listingId) return;
    const byId = {};
    entries.forEach((e) => {
      byId[e.products_suppliers_id] = { selected_quantity: e.selected_quantity, priority: e.priority };
    });
    setUpdateListingPage((prev) => ({
      ...prev,
      editListingSupplierSelection: { ...prev.editListingSupplierSelection, [listingId]: byId },
      showEditListingSuppliersModal: false
    }));
  };

  const handleCloseEditSuppliersModal = () => {
    setUpdateListingPage((prev) => ({ ...prev, showEditListingSuppliersModal: false }));
  };

  const listingLabel = (l) => l.title || `Listing #${l.id}`;
  const loadingDetail = (!!selectedListing && (mlbItemLoading || productLoading)) || false;

  return (
    <div className="page-dark">
      <Container>
        <h1 className="page-title">Update listing</h1>
        <p className="page-subtitle">Select an existing listing to view and edit its current state on MLB.</p>

        <div className="page-content">
          <Form.Group className="mb-4">
            <Form.Label>Listing</Form.Label>
            <DropdownField
              ref={listingDropdownRef}
              value={listingSearchQuery}
              onChange={handleListingSearchChange}
              placeholder="Search by listing title or ID..."
              disabled={listingsLoading}
              loading={listingsLoading}
              loadingMessage="Loading listings..."
              showDropdown={showListingDropdown && !listingsLoading}
              options={filteredListings}
              emptyMessage={listingSearchQuery.trim() ? 'No matching listings' : 'Type to search listings'}
              getLabel={listingLabel}
              getItemKey={(l) => l.id}
              onSelect={handleSelectListing}
              onFocus={() => setUpdateListingPage((prev) => ({ ...prev, showListingDropdown: true }))}
              onClick={() => setUpdateListingPage((prev) => ({ ...prev, showListingDropdown: true }))}
              id="update-listing-dropdown"
            />
          </Form.Group>
        </div>

        {selectedListing && loadingDetail && (
          <div className="d-flex align-items-center gap-2 mb-3">
            <Spinner animation="border" size="sm" variant="light" aria-hidden />
            <span className="text-muted">Loading listing and product…</span>
          </div>
        )}

        {selectedListing && mlbItemError && (
          <div className="alert alert-danger mb-3" role="alert">
            {mlbItemError.message}
          </div>
        )}

        {selectedListing && !loadingDetail && !mlbItemError && (
          <>
            <PickListingSuppliersModal
              show={showEditListingSuppliersModal}
              onHide={handleCloseEditSuppliersModal}
              eligibleProductSuppliers={eligibleProductSuppliers}
              initialSelection={initialSelectionForEditModal}
              onSave={handleSaveEditSuppliersModal}
              modalTitle="Edit listing suppliers"
            />
            <div className="row g-3">
              <div className="col-12 col-lg-5">
                <InfoTableCard title="Basic information" rows={basicInfoRows} />
                <InfoTableCard title="Classification" rows={classificationRows} />
              </div>
              <div className="col-12 col-lg-7">
                <Card className="mb-3">
                  <Card.Header className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <span>MLB listing (current state)</span>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="btn-card-header"
                      onClick={handleOpenEditSuppliersModal}
                      disabled={eligibleProductSuppliers.length === 0}
                    >
                      Edit listing suppliers
                    </Button>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <p className="small text-muted mb-2 px-3 pt-3">
                      Blue = only in product DB, Amber = only on MLB, Purple = both but value differs. Title compared to product name+brand+model. Pictures are from our DB (source of truth).
                    </p>
                    {listingPictures.length > 0 ? (
                      <ListingPictureCarousel
                        pictures={listingPictures}
                        currentIndex={listingPictureIndex}
                        onPrevious={() => setListingPictureIndex((i) => (i <= 0 ? listingPictures.length - 1 : i - 1))}
                        onNext={() => setListingPictureIndex((i) => (i >= listingPictures.length - 1 ? 0 : i + 1))}
                      />
                    ) : (
                      <p className="small text-muted mb-2 px-3 pt-3">No pictures for this product.</p>
                    )}
                    <Table className="info-table mb-0" size="sm" responsive>
                      <tbody>
                        {mlbItemRows.map((r, idx) => (
                          <tr key={r.diffState != null ? `${r.label}-${idx}` : r.label} className={r.diffState ? `diff-${r.diffState.replace('_', '-')}` : undefined}>
                            <td className="info-table-label">{r.label}</td>
                            <td className="info-table-value">{r.value ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    {attributesWithDiff.length > 0 && (
                      <>
                        <hr className="my-2 mx-3 border-secondary" />
                        <p className="small text-muted mb-1 px-3 pt-2">Attributes</p>
                        <Table className="info-table mb-0" size="sm" responsive>
                          <thead>
                            <tr>
                              <th className="info-table-label">ID</th>
                              <th className="info-table-label">Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {attributesWithDiff.map((a, idx) => (
                              <tr key={a.id + String(idx)} className={a.diffState ? `diff-${a.diffState.replace('_', '-')}` : undefined}>
                                <td className="info-table-value">{a.id}</td>
                                <td className="info-table-value">{a.value}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </>
                    )}
                  </Card.Body>
                </Card>
              </div>
            </div>
          </>
        )}
      </Container>
    </div>
  );
}
